import { Component, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReportService } from '../../core/services/report.service';
import { AuthService } from '../../core/services/auth.service';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent, SpinnerComponent],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.css']
})
export class ReportsComponent implements OnInit {
  private reportService = inject(ReportService);
  private auth = inject(AuthService);

  readonly isAdmin = computed(() =>
    this.auth.isSuperAdminSignal() || this.auth.isTenantAdminSignal());

  isLoading = false;
  errorMessage = '';
  selectedReportType = 'financial';
  
  // Date filters
  startDate: string = '';
  endDate: string = '';
  memberId: string = '';
  
  // Report data
  reportData: any = null;

  ngOnInit(): void {
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    this.startDate = firstDay.toISOString().split('T')[0];
    this.endDate = lastDay.toISOString().split('T')[0];

    // Default users only see "My Payment History".
    if (!this.isAdmin()) {
      this.selectedReportType = 'payment-history';
    }
  }

  generateReport(): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select start and end dates';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.reportData = null;

    let reportObservable: any;

    switch (this.selectedReportType) {
      case 'financial':
        reportObservable = this.reportService.getFinancialReport(this.startDate, this.endDate);
        break;
      case 'payment-history':
        reportObservable = this.reportService.getPaymentHistory(
          this.memberId || undefined,
          this.startDate,
          this.endDate
        );
        break;
      case 'members':
        reportObservable = this.reportService.getMemberReport(this.memberId || undefined);
        break;
      default:
        this.errorMessage = 'Invalid report type';
        this.isLoading = false;
        return;
    }

    reportObservable.subscribe({
      next: (response: any) => {
        if (response.success) {
          this.reportData = response.data || response;
        } else {
          this.errorMessage = response.message || 'Failed to generate report';
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        this.errorMessage = error.error?.message || 'Failed to generate report';
        this.isLoading = false;
        console.error('Error generating report:', error);
      }
    });
  }

  exportReport(format: 'pdf' | 'excel'): void {
    if (!this.reportData) {
      this.errorMessage = 'Please generate a report first';
      return;
    }

    this.isLoading = true;
    const params = {
      startDate: this.startDate,
      endDate: this.endDate,
      memberId: this.memberId || undefined
    };

    this.reportService.exportReport(this.selectedReportType, format, params).subscribe({
      next: (blob) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `report_${this.selectedReportType}_${new Date().getTime()}.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to export report';
        this.isLoading = false;
        console.error('Error exporting report:', error);
      }
    });
  }
}
